<template>
  <div class="profile-page">
    <h1>My Profile</h1>
    
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      Loading profile information...
    </div>
    
    <div v-else-if="error" class="error">
      {{ error }}
      <button @click="fetchProfileData" class="retry-button">Try Again</button>
    </div>
    
    <div v-else class="profile-container">
      <!-- User Information -->
      <div class="profile-section">
        <h2>Personal Information</h2>
        <div class="profile-info">
          <div class="info-item">
            <label>Username:</label>
            <span>{{ user.username }}</span>
          </div>
          <div class="info-item">
            <label>Email:</label>
            <span>{{ user.email }}</span>
          </div>
        </div>
      </div>

      <!-- Admin Section - All Users Table -->
      <div v-if="user && user.role === 'admin'" class="profile-section admin-section">
        <h2>All Users Information</h2>
        <div class="table-responsive">
          <table class="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="userData in allUsersData" :key="userData.user.id">
                <td>{{ userData.user.username }}</td>
                <td>{{ userData.user.email }}</td>
                <td>{{ userData.user.name }}</td>
                <td>{{ userData.user.role }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';

// Configure axios to include credentials
axios.defaults.withCredentials = true;

export default {
  name: 'Profile',
  data() {
    return {
      user: null,
      loading: true,
      error: null,
      allUsersData: [] // New data property for admin view
      ,
      userRequests: []
    };
  },
  created() {
    this.fetchProfileData();
  },
  methods: {
    async fetchProfileData() {
      this.loading = true;
      this.error = null;

      try {
        // Fetch user data
        const userResponse = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`);

        if (userResponse.data.user) {
          this.user = userResponse.data.user;
          
          // If user is admin, fetch all users data
          if (this.user.role === 'admin') {
            await this.fetchAllUsersData();
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
        this.error = 'Failed to load profile information. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    
  async fetchAllUsersData() {
      try {
        // Check if user is admin first
        const isAdmin = await this.checkUserRole();
        
        if (!isAdmin) {
          console.error('Unauthorized: Admin access required');
          this.error = 'Unauthorized: Admin access required';
          return;
        }

        // Fetch all users data
        const usersResponse = await axios.get(
          `${config.apiBaseUrl}/api/auth/users`,
          { withCredentials: true }
        );
        
        // Transform the data and filter out the admin user
        this.allUsersData = usersResponse.data
          .filter(user => user.role !== 'admin') // Filter out all admin users
          .map(user => ({
            user: {
              id: user.id,
              username: user.username || '-',
              email: user.email || '-',
              name: user.name || '-',
              role: user.role || 'user'
            }
          }));
        
        console.log('All Users Data:', this.allUsersData);
        
      } catch (error) {
        console.error('Error fetching all users data:', error);
        this.error = 'Failed to load all users information.';
      }
    },

    async checkUserRole() {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}/api/auth/check-session`,
          { withCredentials: true }
        );
        // avoid optional chaining for compatibility with older babel configs
        return response && response.data && response.data.user && response.data.user.role === 'admin';
      } catch (error) {
        console.error('Error checking user role:', error);
        return false;
      }
    },

    async fetchUserRequests() {
      try {
        const resp = await axios.get(`${config.apiBaseUrl}/api/profile/image-search-requests`, { withCredentials: true });
        console.log('fetchUserRequests response:', resp && resp.data);
        this.userRequests = resp.data || [];
      } catch (err) {
        console.error('Failed to fetch user image requests', err);
      }
    },

    imageUrl(req) {
      try {
        return (config && config.apiBaseUrl ? config.apiBaseUrl : '') + (req.image_path || '');
      } catch (e) {
        return req.image_path || '';
      }
    }
  }
}
</script>

<style scoped>
.profile-page {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

h1 {
  margin-bottom: 30px;
  color: #333;
  font-size: 24px;
}

.profile-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

h2 {
  margin-bottom: 20px;
  color: #333;
  font-size: 18px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.profile-info,
.address-info {
  display: grid;
  gap: 15px;
}

.info-item {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
}

.info-item label {
  color: #666;
  font-weight: 500;
}

.info-item span {
  color: #333;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #721c24;
  background: #f8d7da;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.retry-button {
  margin-top: 15px;
  padding: 10px 20px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #c82333;
}

.no-address {
  text-align: center;
  padding: 20px;
  color: #666;
}

.add-address-btn {
  display: inline-block;
  margin-top: 15px;
  padding: 10px 20px;
  background: #4CAF50;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.add-address-btn:hover {
  background: #45a049;
}

@media (max-width: 768px) {
  .info-item {
    grid-template-columns: 1fr;
    gap: 5px;
  }
  
  .profile-page {
    padding: 15px;
  }
  
  .profile-section {
    padding: 15px;
  }
}

/* New styles for admin table */
.admin-section {
  margin-top: 30px;
}

.table-responsive {
  overflow-x: auto;
  margin-top: 20px;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  font-size: 14px;
}

.users-table th,
.users-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.users-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.users-table tr:hover {
  background-color: #f5f5f5;
}

.users-table td {
  color: #666;
}

/* Responsive table */
@media (max-width: 1024px) {
  .table-responsive {
    margin: 0 -20px;
    padding: 0 20px;
  }
  
  .users-table {
    font-size: 13px;
  }
  
  .users-table th,
  .users-table td {
    padding: 10px 8px;
  }
}

.profile-actions {
  display: none;
}

.change-password-btn {
  display: none;
}
</style> 